import { Box, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';

const StringField = ({
  name,
  schema,
  uiSchema = {},
  value,
  onChange,
  onBlur,
  error,
  touched,
  required = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const { title, description, pattern } = schema;
  const fieldId = `field-${name}`;

  const labelText = title || name;
  const widget = uiSchema['ui:widget'];
  const multiline = widget === 'textarea' || (!widget && (
    schema.format === 'textarea' || ['prompt', 'script', 'content', 'text'].includes(name.split('.').pop())
  ));

  const handleChange = useCallback(
    (e) => {
      onChange(name, e.target.value);
    },
    [name, onChange]
  );

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);
      onBlur(name, e.target.value);
    },
    [name, onBlur]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const helperText = useMemo(() => {
    if (touched && error) return error;
    if (isFocused && pattern) return `Pattern: ${pattern}`;
    return description || '';
  }, [touched, error, isFocused, pattern, description]);

  return (
    <TextField
      fullWidth
      multiline={multiline}
      minRows={multiline ? 4 : undefined}
      maxRows={multiline ? 14 : undefined}
      id={fieldId}
      name={name}
      label={labelText}
      value={value || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      error={touched && !!error}
      required={required}
      helperText={
        helperText ? (
          <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
            {helperText}
          </Box>
        ) : (
          ''
        )
      }
      aria-invalid={touched && !!error}
      InputLabelProps={{ shrink: true }}
    />
  );
};

StringField.propTypes = {
  name: PropTypes.string.isRequired,
  schema: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    pattern: PropTypes.string,
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  error: PropTypes.string,
  touched: PropTypes.bool,
  required: PropTypes.bool,
};

export default React.memo(StringField);
